import { useState, useEffect } from 'react';
import { Loader2, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../utils/axiosConfig';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

function formatMin(sec) {
  if (sec == null) return '—';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function ActivitySkillStimulusCard({ activityId, sportType }) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [err, setErr] = useState(null);

  const isRun =
    sportType &&
    (String(sportType).includes('Run') || sportType === 'VirtualRun' || sportType === 'Treadmill');

  useEffect(() => {
    if (!activityId || !isRun) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const res = await api.get(`/api/strava/activity/${activityId}/skill-effects`);
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.error || 'Error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activityId, isRun]);

  if (!isRun) return null;

  if (loading) {
    return (
      <Card className='mb-6 border-border bg-card'>
        <CardContent className='flex items-center justify-center py-10'>
          <Loader2 className='h-8 w-8 animate-spin text-primary' />
        </CardContent>
      </Card>
    );
  }

  if (err || !data) {
    return null;
  }

  const { effects = [], segments = [], compositeIndex, totalMinutes } = data;
  const top = [...effects].slice(0, 8);
  const maxLoad = top.length ? Math.max(...top.map((e) => parseFloat(e.load_units))) : 1;

  return (
    <Card className='mb-6 border-border bg-card'>
      <CardHeader className='pb-2'>
        <CardTitle className='text-lg flex items-center gap-2 text-card-foreground'>
          <Sparkles className='h-5 w-5 text-primary' />
          {t('activityDetail.skillStimulus.title')}
        </CardTitle>
        <p className='text-xs text-muted-foreground'>{t('activityDetail.skillStimulus.subtitle')}</p>
      </CardHeader>
      <CardContent className='space-y-4'>
        {compositeIndex != null && (
          <div className='rounded-lg bg-muted/40 border border-border px-4 py-3 flex flex-wrap items-baseline gap-2'>
            <span className='text-sm text-muted-foreground'>{t('activityDetail.skillStimulus.composite')}</span>
            <span className='text-2xl font-semibold text-foreground tabular-nums'>
              {Number(compositeIndex).toFixed(2)}
            </span>
            {!!totalMinutes && (
              <span className='text-xs text-muted-foreground'>
                {t('activityDetail.skillStimulus.overMinutes', { n: Math.round(totalMinutes) })}
              </span>
            )}
          </div>
        )}

        <div className='space-y-2'>
          {top.map((e) => {
            const label = t(`skills.leaf.${e.skill_key}`);
            const w = maxLoad > 0 ? (parseFloat(e.load_units) / maxLoad) * 100 : 0;
            return (
              <div key={e.skill_id}>
                <div className='flex justify-between text-xs mb-0.5'>
                  <span className='text-foreground font-medium truncate pr-2'>{label}</span>
                  <span className='text-muted-foreground tabular-nums shrink-0'>
                    {parseFloat(e.load_units).toFixed(1)}
                  </span>
                </div>
                <div className='h-2 rounded-full bg-muted overflow-hidden'>
                  <div
                    className={cn('h-full rounded-full bg-primary/80')}
                    style={{ width: `${Math.min(100, w)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {segments.length > 0 && (
          <div>
            <Button variant='ghost' size='sm' className='px-0 h-auto' onClick={() => setExpanded((v) => !v)}>
              {expanded ? <ChevronUp className='h-4 w-4 mr-1' /> : <ChevronDown className='h-4 w-4 mr-1' />}
              {t('activityDetail.skillStimulus.segments')}
            </Button>
            {expanded && (
              <div className='mt-2 overflow-x-auto rounded-md border border-border'>
                <table className='w-full text-xs'>
                  <thead>
                    <tr className='border-b border-border bg-muted/40 text-muted-foreground text-left'>
                      <th className='p-2 font-medium'>#</th>
                      <th className='p-2 font-medium'>{t('activityDetail.skillStimulus.colStimulus')}</th>
                      <th className='p-2 font-medium text-right'>{t('activityDetail.skillStimulus.colTime')}</th>
                      <th className='p-2 font-medium text-right'>{t('activityDetail.skillStimulus.colConf')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {segments.map((seg, i) => (
                      <tr key={seg.id || i} className='border-b border-border/50'>
                        <td className='p-2 text-muted-foreground'>{i + 1}</td>
                        <td className='p-2 text-foreground'>
                          {t(`skills.stimulus.${seg.stimulus_key}`)}
                        </td>
                        <td className='p-2 text-right tabular-nums'>
                          {formatMin(seg.end_offset_sec - seg.start_offset_sec)}
                        </td>
                        <td className='p-2 text-right text-muted-foreground'>
                          {seg.confidence != null ? `${Math.round(seg.confidence * 100)}%` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
